import {
  useAirportOperationsRpt,
  useAirportOperationsRptExport,
  useAirportOperationsSummaryRpt,
} from '@services/report'
import Flex from '@shared/Flex'
import { Button, Input } from '@shared/Form'
import useSearch from '@shared/innmaxLib/hooks/useSearch'
import { RangePicker } from '@shared/innmaxUI/Form/DatePicker'
import Layout from '@shared/Layout'
import Table from '@shared/Table'
import {
  convertTimestampToDuration,
  convertTimestampToMinutes,
  downloadFile,
  toThousandSeparator,
} from '@shared/utils/webHelper'
import { Col, ColProps, Form, Row, Spin } from 'antd'
import { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { pathOr } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'src/components/Form'
import OptionsSelect from 'src/components/OptionsSelect'

type ISummaryKeyMap = {
  label: string
  dataIndex?: string
  span?: number
  titleColProps?: ColProps
  valueColProps?: ColProps
  render?: (value: any) => React.ReactNode
}

const initialReportType = {
  startAt: moment().startOf('month').valueOf(),
  endAt: moment().endOf('month').valueOf(),
}

function Operations() {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const prevQueryRef = React.useRef<any>({})

  const { query, handleSearch, handleTableChange, isTriggerSearch } = useSearch(
    {
      form,
      initial: { ...initialReportType },
      dateRangeField: ['startAt', 'endAt'],
    }
  )

  const {
    data: summaryData,
    refetch: refetchSummary,
    loading: summaryLoading,
  } = useAirportOperationsSummaryRpt(query)

  React.useEffect(() => {
    if (!isTriggerSearch) {
      return
    }
    const { reactQueryCache: prevCache, ...prevRest } = prevQueryRef.current
    const { reactQueryCache: currentCache, ...currentRest } = query
    const isEqual = JSON.stringify(prevRest) === JSON.stringify(currentRest)
    if (!isEqual) {
      refetchSummary(query)
    }
    prevQueryRef.current = query
  }, [query, isTriggerSearch])

  const [exporting, handleExport] = useAirportOperationsRptExport(query, {
    onSuccess: (blob: any) => {
      downloadFile({
        blob,
        filename: 'OperationReport',
      })
    },
  })

  const { dataSource, loading } = useAirportOperationsRpt(query, {
    enabled: isTriggerSearch,
  })

  const columns: ColumnsType<any> = [
    {
      title: '地勤公司',
      dataIndex: 'orgSessionName',
      align: 'center',
      width: 200,
    },
    ...(query.orgSessionId
      ? [
          {
            title: '車牌',
            width: 200,
            dataIndex: 'lprNumber',
            align: 'center' as const,
          },
        ]
      : []),
    {
      title: '充電度數(一般時段)',
      width: 200,
      dataIndex: 'offPeakDegree',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '充電度數(壅塞)',
      width: 200,
      dataIndex: 'peakDegree',
      align: 'center',
      render: v => toThousandSeparator(v),
    },
    {
      title: '充電時長(一般時段)',
      dataIndex: 'offPeakDuration',
      align: 'center',
      width: 200,
      render: v => convertTimestampToDuration(v),
    },
    {
      title: '充電時長(壅塞)',
      dataIndex: 'peakDuration',
      align: 'center',
      width: 200,
      render: v => convertTimestampToDuration(v),
    },
    {
      title: '充電費(一般時段)',
      dataIndex: 'offPeakFee',
      align: 'center',
      width: 200,
      render: v => toThousandSeparator(v),
    },
    {
      title: '充電費(壅塞)',
      dataIndex: 'peakFee',
      align: 'center',
      width: 200,
      render: v => toThousandSeparator(v),
    },
    {
      title: '佔用時間',
      dataIndex: 'occupationDuration',
      align: 'center',
      width: 200,
      render: v => convertTimestampToDuration(v, 'H時:m分'),
    },
    {
      title: '佔用費',
      dataIndex: 'occupationFee',
      align: 'center',
      width: 200,
      render: v => toThousandSeparator(v),
    },
    ...(query.orgSessionId
      ? []
      : [
          {
            title: '優惠費率',
            dataIndex: 'discountFee',
            align: 'center' as const,
            width: 200,
            render: (v: any) => toThousandSeparator(v),
          },
          {
            title: '優惠費用',
            dataIndex: 'discountAmount',
            align: 'center' as const,
            width: 200,
            render: (v: any) => toThousandSeparator(v),
          },
          {
            title: '短溢收金額',
            dataIndex: 'adjustAmount',
            align: 'center' as const,
            width: 200,
            render: (v: any) => toThousandSeparator(v),
          },
        ]),
    {
      title: '總費用',
      dataIndex: 'totalAmount',
      align: 'center',
      width: 200,
      render: v => toThousandSeparator(v),
    },
  ]

  const getOrgSessionGroupReportSummaryMaps = () => {
    const maps: ISummaryKeyMap[][] = [
      [
        { label: '充電總度數', dataIndex: 'chargingDegree' },
        { label: '一般時段', dataIndex: 'offPeakFee' },
        { label: '壅塞時段', dataIndex: 'peakFee' },
        { label: '充電總費用', dataIndex: 'chargeFee' },
      ],
      [
        {
          label: '佔用時數/分',
          dataIndex: 'occupationDuration',
          render: r =>
            toThousandSeparator(
              convertTimestampToMinutes(r?.occupationDuration)
            ),
        },
        {
          label: '佔用費用',
          dataIndex: 'occupationFee',
        },
        { label: '優惠費用', dataIndex: 'discountAmount' },
        {
          label: '實際費用',
          dataIndex: 'totalAmount',
        },
      ],
      [
        {
          label: ' ',
          titleColProps: { span: 12 },
        },
        {
          label: '短溢收',
          dataIndex: 'adjustAmount',
        },
      ],
      [
        { label: '會計科目', span: 6 },
        { label: '電費分攤', dataIndex: 'electricityCost' },
        { label: '維護費分攤', dataIndex: 'maintenanceCost' },
        { label: '外包費分攤', dataIndex: 'outsourcingCost' },
      ],
    ]
    return maps
  }

  const getOrgSessionRawDataReportSummaryMaps = () => {
    const maps: ISummaryKeyMap[][] = [
      [
        { label: '充電總度數', dataIndex: 'chargingDegree' },
        { label: '一般時段', dataIndex: 'offPeakFee' },
        { label: '壅塞時段', dataIndex: 'peakFee' },
        { label: '充電總費用', dataIndex: 'chargeFee' },
      ],
      [
        {
          label: '佔用時數/分',
          dataIndex: 'occupationDuration',
          titleColProps: { span: 6 },
          valueColProps: { span: 6 },
          render: r =>
            toThousandSeparator(
              convertTimestampToMinutes(r?.occupationDuration)
            ),
        },
        {
          label: '佔用費用',
          dataIndex: 'occupationFee',
          titleColProps: { span: 6 },
          valueColProps: { span: 6 },
        },
      ],
      [
        {
          label: '優惠費率',
          dataIndex: 'discountFee',
          titleColProps: { span: 6 },
          valueColProps: { span: 6 },
        },
        {
          label: '優惠費用',
          dataIndex: 'discountAmount',
          titleColProps: { span: 6 },
          valueColProps: { span: 6 },
        },
      ],
      [
        {
          label: ' ',
          titleColProps: { span: 12 },
        },
        {
          label: '短溢收',
          dataIndex: 'adjustAmount',
          titleColProps: { span: 6 },
          valueColProps: { span: 6 },
        },
      ],
      [
        {
          label: '實際費用(充電總費用+佔用費用-優惠費用+短溢收)',
          dataIndex: 'totalAmount',
          titleColProps: { span: 10, offset: 6 },
          valueColProps: { span: 6 },
        },
      ],
    ]
    return maps
  }

  const renderSummary = () => {
    let _maps: ISummaryKeyMap[][] = []
    if (query.orgSessionId) {
      _maps = getOrgSessionRawDataReportSummaryMaps()
    } else {
      _maps = getOrgSessionGroupReportSummaryMaps()
    }
    if (summaryLoading) {
      return <Spin />
    }

    return _maps?.map((map, rowIndex) => (
      <React.Fragment key={rowIndex}>
        <Row className="w-full">
          {map.map(
            (
              {
                label,
                dataIndex,
                titleColProps = { span: 3 },
                valueColProps = { span: 3 },
                render,
              },
              index
            ) => {
              const value = pathOr('-', [dataIndex || 'unknown'], summaryData)
              return (
                <React.Fragment key={index}>
                  <Col {...titleColProps}>{label}</Col>
                  {dataIndex && (
                    <Col className="value" {...valueColProps}>
                      {render
                        ? render(summaryData)
                        : isNaN(Number(value))
                        ? value
                        : toThousandSeparator(value)}
                    </Col>
                  )}
                </React.Fragment>
              )
            }
          )}
        </Row>
        {rowIndex < _maps?.length - 1 && (
          <div className="h-2 w-full my-10 bg-white opacity-40 rounded-full" />
        )}
      </React.Fragment>
    ))
  }

  return (
    <>
      <Layout>
        <Form layout="inline" form={form}>
          <FormItem
            label={'關鍵字'}
            name="keyword"
            tooltip="支援車牌,EVCCID,設備名稱">
            <Input placeholder="車牌,EVCCID,設備名稱" />
          </FormItem>
          <FormItem label={'地勤公司'} name="orgSessionId">
            <OptionsSelect.CompaniesSelect />
          </FormItem>
          <FormItem
            label={'時間範圍'}
            name="date"
            initialValue={[
              moment(initialReportType.startAt),
              moment(initialReportType.endAt),
            ]}>
            <RangePicker />
          </FormItem>

          <FormItem.Button>
            <Button
              loading={loading}
              type="primary"
              className="w-96"
              onClick={() => {
                handleSearch()
              }}>
              {t('common:search')}
            </Button>
          </FormItem.Button>
          <FormItem.Button>
            <Button
              loading={exporting}
              type="primary"
              ghost
              className="w-96"
              onClick={handleExport}>
              {t('report:export')}
            </Button>
          </FormItem.Button>
        </Form>
        <div className="hidden md:block p-15 text-sm rounded-lg bg-[#052A3A] mt-20 text-[#A1DDFF] [&_.value]:text-white">
          {renderSummary()}
        </div>
        <div className="mt-44 mb-16">
          <Flex between>
            <Table.RowCount count={dataSource?.total || 0} />
          </Flex>
        </div>
        <Table
          simpleMode
          rowKey={r => `${r.lprNumber}${r.chargeAt}`}
          columns={columns}
          loading={loading}
          dataSource={dataSource}
          onChange={handleTableChange}
          scroll={{
            x: 1200,
          }}
        />
      </Layout>
    </>
  )
}

export default React.memo(Operations)
